/**
 * LockedFeatureCard Component
 * Generic feature gating wrapper with consistent UX across the app.
 *
 * Design Principles:
 * - Single responsibility: presentation + gating only, NOT data fetching
 * - Works with both client-side entitlements AND server-side 403 signals
 * - Respects grace periods (canceled but still in paid period = unlocked)
 * - No console noise in production
 * - Consistent CTA routing to /shop
 *
 * Usage:
 *   <LockedFeatureCard
 *     title="See Who Liked You"
 *     description="Find out who's interested before you swipe"
 *     requiredTier="elite"
 *     user={user}
 *   >
 *     <WhoLikedMeList />
 *   </LockedFeatureCard>
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Lock, Sparkles, AlertTriangle, CreditCard } from 'lucide-react';
import { isEliteEligible, getEliteEligibilityStatus, getEliteGateMessage } from '../../utils/eliteGate';

// Tier configurations
const TIER_CONFIG = {
  elite: {
    name: 'Elite',
    badge: '👑',
    icon: Crown,
    gradient: 'from-amber-500 to-yellow-500',
    bgGradient: 'from-amber-50 to-yellow-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-800',
    benefits: [
      'See who liked you',
      'More likes daily',
      'Priority matching',
      'Elite badge on profile',
      'Ad-free experience',
    ],
  },
  // Future tiers can be added here
  wingman_pro: {
    name: 'Wingman Pro',
    badge: '🤖',
    icon: Sparkles,
    gradient: 'from-purple-500 to-indigo-500',
    bgGradient: 'from-purple-50 to-indigo-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-800',
    benefits: [
      'AI suggestions included',
      'Profile optimization',
      'Chat opener generator',
      'Tone matching',
    ],
  },
};

// Debug logging (silent in production)
const debugLog = (...args) => {
  if (process.env.NODE_ENV !== 'production' && localStorage.getItem('debug_locked_features') === '1') {
    console.info('[LockedFeatureCard]', ...args);
  }
};

/**
 * Skeleton placeholder for loading state
 */
const LockedFeatureSkeleton = ({ className = '' }) => (
  <div className={`animate-pulse ${className}`}>
    <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8">
      <div className="flex flex-col items-center">
        {/* Icon skeleton */}
        <div className="w-20 h-20 bg-gray-300 rounded-full mb-6" />
        {/* Title skeleton */}
        <div className="h-8 w-48 bg-gray-300 rounded mb-3" />
        {/* Description skeleton */}
        <div className="h-4 w-64 bg-gray-300 rounded mb-2" />
        <div className="h-4 w-56 bg-gray-300 rounded mb-6" />
        {/* Button skeleton */}
        <div className="h-12 w-40 bg-gray-300 rounded-xl" />
      </div>
    </div>
  </div>
);

/**
 * Locked state UI - premium feel, not "access denied"
 */
const LockedUI = ({
  title,
  description,
  tierConfig,
  ctaText,
  ctaHref,
  onCtaClick,
}) => {
  const TierIcon = tierConfig.icon;

  return (
    <div
      className={`flex flex-col items-center justify-center p-8 text-center bg-gradient-to-br ${tierConfig.bgGradient} rounded-2xl border ${tierConfig.borderColor}`}
      data-testid="locked-feature-card"
    >
      {/* Lock icon with tier styling */}
      <div className={`w-20 h-20 bg-gradient-to-br ${tierConfig.gradient} rounded-full flex items-center justify-center mb-6 shadow-lg`}>
        <Lock className="w-10 h-10 text-white" />
      </div>

      {/* Tier badge */}
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white dark:bg-slate-800 shadow-sm mb-4`} data-allow-white="1">
        <TierIcon className={`w-4 h-4 ${tierConfig.textColor}`} />
        <span className={`text-sm font-semibold ${tierConfig.textColor}`}>
          {tierConfig.name} Feature
        </span>
      </div>

      {/* Title */}
      <h2 className="font-playfair text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
        {title}
      </h2>

      {/* Description - confident, not guilt-inducing */}
      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
        {description}
      </p>

      {/* CTA Button */}
      <button
        onClick={onCtaClick}
        className={`inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r ${tierConfig.gradient} text-white font-semibold rounded-xl shadow-md hover:opacity-90 transition-all duration-200 transform hover:scale-105`}
        data-testid="locked-feature-cta"
      >
        <TierIcon className="w-5 h-5 mr-2" />
        {ctaText}
      </button>

      {/* Benefits preview */}
      <div className={`mt-8 p-4 bg-white/60 rounded-xl max-w-md border ${tierConfig.borderColor}`}>
        <h3 className={`font-semibold ${tierConfig.textColor} mb-2`}>
          {tierConfig.name} Benefits:
        </h3>
        <ul className={`text-sm ${tierConfig.textColor} space-y-1 text-left`}>
          {tierConfig.benefits.map((benefit, idx) => (
            <li key={idx}>✓ {benefit}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/**
 * LockedFeatureCard - Main Component
 *
 * @param {string} title - Feature title (shown when locked)
 * @param {string} description - Feature description (shown when locked)
 * @param {string} requiredTier - Required tier: "elite" | "wingman_pro"
 * @param {string} ctaText - CTA button text (default: "Upgrade to {Tier}")
 * @param {string} ctaHref - CTA destination (default: "/shop")
 * @param {boolean} isUnlocked - Client-side unlock state (from entitlements) - DEPRECATED, use `user` prop
 * @param {boolean} locked - Server-side lock state (from API 403)
 * @param {boolean} loading - Show skeleton while loading
 * @param {Object} user - User object with entitlements (recommended)
 * @param {boolean} showGraceWarning - Show warning banner when in grace period (default: true)
 * @param {React.ReactNode} children - Content to show when unlocked
 */
const LockedFeatureCard = ({
  title,
  description,
  requiredTier = 'elite',
  ctaText,
  ctaHref = '/shop',
  isUnlocked,
  locked,
  loading = false,
  user,
  showGraceWarning = true,
  children,
  className = '',
}) => {
  const navigate = useNavigate();

  // Get tier configuration (fallback to elite if unknown)
  const tierConfig = TIER_CONFIG[requiredTier] || TIER_CONFIG.elite;

  // Default CTA text
  const finalCtaText = ctaText || `Upgrade to ${tierConfig.name}`;

  // Handle CTA click
  const handleCtaClick = () => {
    debugLog('CTA clicked, navigating to:', ctaHref);
    navigate(`${ctaHref}?focus=${requiredTier}&reason=locked_feature`);
  };

  // Handle update payment click (for grace period)
  const handleUpdatePayment = () => {
    // Route to shop since billing management isn't implemented yet
    navigate('/shop?reason=grace_renewal');
  };

  // Loading state → skeleton
  if (loading) {
    debugLog('Rendering skeleton (loading=true)');
    return <LockedFeatureSkeleton className={className} />;
  }

  // Determine lock state using grace-aware logic
  let isLocked = false;
  let inGrace = false;
  let gateMessage = null;

  if (typeof locked === 'boolean') {
    // Server-side signal takes precedence
    isLocked = locked;
    debugLog('Using locked prop:', locked);
  } else if (user && requiredTier === 'elite') {
    // Use grace-aware eligibility check for Elite tier
    const eligible = isEliteEligible(user);
    const status = getEliteEligibilityStatus(user);
    isLocked = !eligible;
    inGrace = status.inGrace && eligible;
    gateMessage = getEliteGateMessage(user);
    debugLog('Using grace-aware check:', { eligible, inGrace, status });
  } else if (typeof isUnlocked === 'boolean') {
    // Legacy: Client-side entitlement (no grace awareness)
    isLocked = !isUnlocked;
    debugLog('Using isUnlocked prop (inverted):', !isUnlocked);
  }
  // If neither prop is provided, assume unlocked (render children)

  // Show locked UI
  if (isLocked) {
    debugLog('Rendering locked UI for tier:', requiredTier);
    return (
      <div className={className}>
        <LockedUI
          title={title}
          description={description}
          tierConfig={tierConfig}
          ctaText={finalCtaText}
          ctaHref={ctaHref}
          onCtaClick={handleCtaClick}
        />
      </div>
    );
  }

  // Show grace period warning + content (user can still access, but payment failed)
  if (inGrace && showGraceWarning && gateMessage) {
    debugLog('Rendering grace period warning');
    
    // Get expiration date for display
    const status = getEliteEligibilityStatus(user);
    const expiresAt = status.expiresAt;
    let expirationText = null;
    if (expiresAt) {
      try {
        const expDate = new Date(expiresAt);
        expirationText = expDate.toLocaleDateString(undefined, { 
          month: 'short', 
          day: 'numeric',
          year: expDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
        });
      } catch {
        // Ignore date parsing errors
      }
    }
    
    return (
      <div className={className}>
        {/* Grace period warning banner */}
        <div
          className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 rounded-r-lg"
          data-testid="grace-period-warning"
        >
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-amber-800 font-semibold text-sm">
                {gateMessage.title}
              </h3>
              <p className="text-amber-700 text-sm mt-1">
                {gateMessage.message}
                {expirationText && (
                  <span className="font-medium"> Access ends on {expirationText}.</span>
                )}
              </p>
              {gateMessage.showUpdatePayment && (
                <button
                  onClick={handleUpdatePayment}
                  className="mt-2 inline-flex items-center text-sm font-medium text-amber-800 hover:text-amber-900"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Renew Membership
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Render protected content */}
        {children}
      </div>
    );
  }

  // Unlocked → render children
  debugLog('Rendering unlocked content');
  return <>{children}</>;
};

export default LockedFeatureCard;
